import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, { Background, Controls } from 'reactflow';
import dagre from 'dagre';
import toast from 'react-hot-toast';
import { Check, Network, Plus, RefreshCw, X } from 'lucide-react';
import 'reactflow/dist/style.css';
import { getCourseModules } from '../../api/modules';
import {
  getCourseAiStatus,
  addModuleSkill,
  removeModuleSkill,
  markModuleSkillsReviewed,
  reviewAllSkills,
  addPrerequisite,
  removePrerequisite,
  markModulePrerequisitesReviewed,
  getSkillTaxonomy,
  triggerReanalysis,
} from '../../api/skills';
import { unwrapList, extractErrorMessage } from '../../utils/api';
import Dropdown from '../../components/Dropdown/Dropdown';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import LoadingState from '../../components/LoadingState/LoadingState';
import Modal from '../../components/Modal/Modal';
import { usePolling } from '../../hooks/usePolling';
import { useI18n } from '../../i18n/I18nContext';
import './SkillsPrerequisitesTab.css';

// ---------------------------------------------------------------------------
// DAG layout helper using dagre
// ---------------------------------------------------------------------------

const NODE_WIDTH = 180;
const NODE_HEIGHT = 50;

function buildDagLayout(modules, t) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 40, ranksep: 80 });

  modules.forEach((m) => {
    g.setNode(m.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  modules.forEach((m) => {
    (m.prerequisites || []).forEach((req) => {
      g.setEdge(req.id, m.id);
    });
  });

  dagre.layout(g);

  const nodes = modules.map((m) => {
    const pos = g.node(m.id);
    return {
      id: m.id,
      type: 'default',
      position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
      data: { label: m.title || t('courses.dagModuleFallback', { index: m.orderIndex + 1 }) },
      style: {
        background: 'var(--color-surface)',
        color: 'var(--color-text)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 600,
        padding: '8px 12px',
        width: NODE_WIDTH,
      },
    };
  });

  const edges = [];
  modules.forEach((m) => {
    (m.prerequisites || []).forEach((req) => {
      edges.push({
        id: `${req.id}->${m.id}`,
        source: req.id,
        target: m.id,
        type: 'smoothstep',
        style: { stroke: 'var(--color-text-subtle)' },
        markerEnd: { type: 'arrowclosed', color: 'var(--color-text-subtle)' },
      });
    });
  });

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Skill chip
// ---------------------------------------------------------------------------

function SkillChip({ skill, onRemove, removing }) {
  const { t } = useI18n();
  return (
    <span className="sp-skill-chip">
      <span className="sp-chip-label">{skill.name || skill.slug}</span>
      <button
        type="button"
        className="sp-chip-remove"
        onClick={() => onRemove(skill.slug)}
        disabled={removing}
        aria-label={t('courses.removeSkillAria', { name: skill.name || skill.slug })}
      >
        <X aria-hidden="true" />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add skill dropdown
// ---------------------------------------------------------------------------

function AddSkillDropdown({ courseId, moduleId, taxonomy, taxonomyUnavailable, onAdded, onClose }) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = taxonomy.filter(
    (s) =>
      s.name.toLowerCase().includes(query.toLowerCase()) ||
      s.slug.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = async (skill) => {
    setAdding(true);
    setError(null);
    try {
      const result = await addModuleSkill(courseId, moduleId, { skillSlug: skill.slug, name: skill.name });
      onAdded(result);
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.addSkillFailed')));
    } finally {
      setAdding(false);
    }
  };

  const handleCreateNew = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const slug = trimmed.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setAdding(true);
    setError(null);
    try {
      const result = await addModuleSkill(courseId, moduleId, { skillSlug: slug, name: trimmed });
      onAdded(result);
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.addSkillFailed')));
    } finally {
      setAdding(false);
    }
  };

  const exactMatch = taxonomy.some(
    (s) => s.slug === query.toLowerCase().replace(/\s+/g, '-') || s.name.toLowerCase() === query.toLowerCase()
  );

  return (
    <div className="sp-add-skill-dropdown" role="dialog" aria-label={t('courses.addSkillDialogAria')}>
      <input
        ref={inputRef}
        type="text"
        className="sp-add-skill-input"
        placeholder={t('courses.searchOrCreateSkillPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          if (e.key === 'Enter' && !exactMatch && query.trim()) handleCreateNew();
        }}
        disabled={adding}
      />
      {error && <div className="sp-add-skill-error">{error}</div>}
      {taxonomyUnavailable && (
        <div className="sp-add-skill-note">
          <strong>{t('courses.skillsCatalogueUnavailableTitle')}</strong>
          <span>{t('courses.skillsCatalogueUnavailableDesc')}</span>
        </div>
      )}
      <ul className="sp-add-skill-list">
        {filtered.map((s) => (
          <li key={s.slug}>
            <button
              type="button"
              className="sp-add-skill-option"
              onClick={() => handleSelect(s)}
              disabled={adding}
            >
              {s.name}
              <span className="sp-add-skill-slug">{s.slug}</span>
            </button>
          </li>
        ))}
        {!exactMatch && query.trim() && (
          <li>
            <button
              type="button"
              className="sp-add-skill-option sp-add-skill-create"
              onClick={handleCreateNew}
              disabled={adding}
            >
              {t('courses.createSkillOption', { query: query.trim() })}
            </button>
          </li>
        )}
        {filtered.length === 0 && !query.trim() && !taxonomyUnavailable && (
          <li className="sp-add-skill-empty">{t('courses.startTypingSkills')}</li>
        )}
      </ul>
      <div className="sp-add-skill-footer">
        <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={onClose}>
          {t('courses.cancel')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Re-analyze rate-limit persistence helpers
// ---------------------------------------------------------------------------

function getRateLimitStorageKey(courseId) {
  return `reanalyze-blocked-${courseId}`;
}

function getStoredUnlockTime(courseId) {
  try {
    const raw = localStorage.getItem(getRateLimitStorageKey(courseId));
    if (!raw) return null;
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return null;
    return ts;
  } catch {
    return null;
  }
}

function setStoredUnlockTime(courseId, unlockMs) {
  try {
    localStorage.setItem(getRateLimitStorageKey(courseId), String(unlockMs));
  } catch {
    // localStorage may be unavailable in some environments — fail silently
  }
}

function clearStoredUnlockTime(courseId) {
  try {
    localStorage.removeItem(getRateLimitStorageKey(courseId));
  } catch {
    // ignore
  }
}

/**
 * Convert a duration in milliseconds to a human-readable phrase.
 * e.g. 75600000 -> "21 hours"
 *      12345000  -> "3 hours and 25 minutes"
 *      59000     -> "less than a minute"
 */
function humanReadableDuration(ms, t) {
  if (ms <= 0) return t('courses.durationNow');
  const totalMinutes = Math.ceil(ms / 60000);
  if (totalMinutes < 1) return t('courses.durationLessThanMinute');
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hourPart = hours === 1 ? t('courses.durationHourOne') : t('courses.durationHoursCount', { count: hours });
  const minutePart = minutes === 1 ? t('courses.durationMinuteOne') : t('courses.durationMinutesCount', { count: minutes });
  if (hours === 0) return minutePart;
  if (minutes === 0) return hourPart;
  return t('courses.durationCombined', { hourPart, minutePart });
}

// ---------------------------------------------------------------------------
// Confirmation modal for re-analysis
// ---------------------------------------------------------------------------

function ReanalyzeModal({ onConfirm, onCancel, submitting }) {
  const { t } = useI18n();
  // The shared Modal traps focus, closes on Esc / the backdrop, locks the page scroll and is a
  // bottom sheet on a phone. Cancel takes the first focus: the safe answer to "run a long job?".
  return (
    <Modal
      open
      onClose={onCancel}
      title={t('courses.reanalyzeModalTitle')}
      size="sm"
      footer={(
        <>
          <button
            type="button"
            className="ds-btn ds-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-autofocus
          >
            {t('courses.cancel')}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn-primary"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting ? t('courses.starting') : t('courses.runAnalysis')}
          </button>
        </>
      )}
    >
      <div className="sp-modal-text">
        <p>
          {t('courses.reanalyzeModalBody1')}
          <strong>{t('courses.reanalyzeModalBodyBold')}</strong>{t('courses.reanalyzeModalBody2')}
        </p>
        <p>{t('courses.reanalyzeModalBody3')}</p>
        <p className="sp-modal-note">{t('courses.reanalyzeModalNote')}</p>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Module skill row
// ---------------------------------------------------------------------------

function ModuleSkillRow({ mod, courseId, taxonomy, taxonomyUnavailable, onModuleUpdate }) {
  const { t } = useI18n();
  const [addingOpen, setAddingOpen] = useState(false);
  const [removingSlug, setRemovingSlug] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState(null);

  const isAiProposed = mod.aiProposedSkills === true && mod.skillsReviewedAt == null;
  const skills = mod.skills || [];

  const handleRemove = async (slug) => {
    setRemovingSlug(slug);
    setError(null);
    try {
      await removeModuleSkill(courseId, mod.id, slug);
      onModuleUpdate(mod.id, (prev) => ({
        ...prev,
        skills: (prev.skills || []).filter((s) => s.slug !== slug),
      }));
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.removeSkillFailed')));
    } finally {
      setRemovingSlug(null);
    }
  };

  const handleAdded = (updatedSkills) => {
    setAddingOpen(false);
    onModuleUpdate(mod.id, (prev) => ({
      ...prev,
      skills: Array.isArray(updatedSkills) ? updatedSkills : updatedSkills?.skills || prev.skills,
    }));
  };

  const handleMarkReviewed = async () => {
    setReviewing(true);
    setError(null);
    try {
      await markModuleSkillsReviewed(courseId, mod.id);
      onModuleUpdate(mod.id, (prev) => ({
        ...prev,
        aiProposedSkills: false,
        skillsReviewedAt: new Date().toISOString(),
      }));
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.markReviewedFailed')));
    } finally {
      setReviewing(false);
    }
  };

  return (
    <div className="sp-module-row">
      <div className="sp-module-header">
        <div className="sp-module-title-block">
          <span className="sp-module-order">{mod.orderIndex + 1}</span>
          <span className="sp-module-title">{mod.title}</span>
          {isAiProposed && (
            <span className="ds-badge ds-badge-neutral" title={t('courses.aiProposedSkillsTooltip')}>
              {t('courses.aiProposedPill')}
            </span>
          )}
          {mod.skillsReviewedAt && (
            <span className="ds-badge ds-badge-success">{t('courses.reviewedPill')}</span>
          )}
        </div>
        <div className="sp-module-actions">
          {isAiProposed && (
            <button
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-sm sp-btn-review"
              onClick={handleMarkReviewed}
              disabled={reviewing}
            >
              {reviewing ? t('courses.marking') : t('courses.markReviewed')}
            </button>
          )}
        </div>
      </div>

      {error && <div className="sp-inline-error">{error}</div>}

      <div className="sp-chip-row">
        {skills.map((skill) => (
          <SkillChip
            key={skill.slug}
            skill={skill}
            onRemove={handleRemove}
            removing={removingSlug === skill.slug}
          />
        ))}
        {skills.length === 0 && !addingOpen && (
          <span className="sp-chip-empty">{t('courses.noSkillsAssigned')}</span>
        )}
        <button
          type="button"
          className="sp-chip-add-btn"
          onClick={() => setAddingOpen(true)}
          aria-label={t('courses.addSkillToModuleAria')}
        >
          <Plus aria-hidden="true" />
          {t('courses.addSkillButton')}
        </button>
      </div>

      {addingOpen && (
        <AddSkillDropdown
          courseId={courseId}
          moduleId={mod.id}
          taxonomy={taxonomy}
          taxonomyUnavailable={taxonomyUnavailable}
          onAdded={handleAdded}
          onClose={() => setAddingOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prerequisites table row
// ---------------------------------------------------------------------------

function PrereqRow({ mod, allModules, courseId, onModuleUpdate }) {
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [selectedReq, setSelectedReq] = useState('');
  const [error, setError] = useState(null);

  const prerequisites = mod.prerequisites || [];
  const candidates = allModules.filter(
    (m) => m.id !== mod.id &&
      m.orderIndex < mod.orderIndex &&
      !prerequisites.some((p) => p.id === m.id)
  );

  const handleAdd = async () => {
    if (!selectedReq) return;
    setAdding(true);
    setError(null);
    try {
      await addPrerequisite(courseId, mod.id, selectedReq);
      const reqMod = allModules.find((m) => m.id === selectedReq);
      onModuleUpdate(mod.id, (prev) => ({
        ...prev,
        prerequisites: [...(prev.prerequisites || []), { id: reqMod.id, title: reqMod.title }],
      }));
      setSelectedReq('');
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.addPrerequisiteFailed')));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (reqId) => {
    setError(null);
    try {
      await removePrerequisite(courseId, mod.id, reqId);
      onModuleUpdate(mod.id, (prev) => ({
        ...prev,
        prerequisites: (prev.prerequisites || []).filter((p) => p.id !== reqId),
      }));
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.removePrerequisiteFailed')));
    }
  };

  const isAiProposed = mod.aiProposedPrerequisites === true && mod.prerequisitesReviewedAt == null;
  const [reviewing, setReviewing] = useState(false);

  const handleMarkReviewed = async () => {
    setReviewing(true);
    setError(null);
    try {
      await markModulePrerequisitesReviewed(courseId, mod.id);
      onModuleUpdate(mod.id, (prev) => ({
        ...prev,
        aiProposedPrerequisites: false,
        prerequisitesReviewedAt: new Date().toISOString(),
      }));
    } catch (err) {
      setError(extractErrorMessage(err, t('courses.markPrerequisitesReviewedFailed')));
    } finally {
      setReviewing(false);
    }
  };

  return (
    <tr className="sp-prereq-row">
      <td className="sp-prereq-module-cell">
        <div className="sp-prereq-module">
          <span className="sp-module-order">{mod.orderIndex + 1}</span>
          <span className="sp-prereq-module-title">{mod.title}</span>
          {isAiProposed && (
            <span className="ds-badge ds-badge-neutral" title={t('courses.aiProposedPrereqTooltip')}>
              {t('courses.aiPillShort')}
            </span>
          )}
        </div>
      </td>
      <td className="sp-prereq-requires-cell" data-label={t('courses.colRequires')}>
        {prerequisites.length === 0 ? (
          <span className="sp-prereq-none">{t('courses.none')}</span>
        ) : (
          <div className="sp-prereq-tags">
            {prerequisites.map((req) => (
              <span key={req.id} className="sp-prereq-tag">
                {req.title}
                <button
                  type="button"
                  className="sp-prereq-tag-remove"
                  onClick={() => handleRemove(req.id)}
                  aria-label={t('courses.removePrerequisiteAria', { title: req.title })}
                >
                  <X aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}
        {error && <div className="sp-inline-error sp-inline-error-sm">{error}</div>}
      </td>
      <td className="sp-prereq-add-cell" data-label={t('courses.colAddPrerequisite')}>
        {candidates.length > 0 ? (
          <div className="sp-prereq-add-row">
            <Dropdown
              value={selectedReq}
              onChange={(val) => setSelectedReq(val)}
              ariaLabel={t('courses.colAddPrerequisite')}
              size="sm"
              options={[
                { value: '', label: t('courses.selectModulePlaceholder') },
                ...candidates.map((c) => ({
                  value: c.id,
                  label: `${c.orderIndex + 1}. ${c.title}`,
                })),
              ]}
              placeholder={t('courses.selectModulePlaceholder')}
              className="sp-prereq-select"
            />
            <button
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-sm sp-prereq-add-btn"
              onClick={handleAdd}
              disabled={adding || !selectedReq}
            >
              {adding ? t('courses.processingEllipsis') : t('courses.addButton')}
            </button>
          </div>
        ) : (
          <span className="sp-prereq-none">{t('courses.noEarlierModules')}</span>
        )}
      </td>
      <td className="sp-prereq-review-cell" data-label={t('courses.colReview')}>
        {isAiProposed ? (
          <button
            type="button"
            className="ds-btn ds-btn-secondary ds-btn-sm sp-btn-review"
            onClick={handleMarkReviewed}
            disabled={reviewing}
          >
            {reviewing ? t('courses.processingEllipsis') : t('courses.markReviewed')}
          </button>
        ) : mod.prerequisitesReviewedAt ? (
          <span className="ds-badge ds-badge-success">{t('courses.reviewedPill')}</span>
        ) : null}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------

const POLLING_INTERVAL = 5000;
const POLLING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function SkillsPrerequisitesTab({ courseId }) {
  const { t } = useI18n();
  const [modules, setModules] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  // The tenant skill taxonomy (GET /skills) has no backing route on this backend today and
  // 404s on every load. That's expected and must not surface as a page-level error — only the
  // Add Skill autocomplete loses its suggestion list, so track it separately and show a calm,
  // non-blocking note right there instead.
  const [taxonomyUnavailable, setTaxonomyUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  // The load failed: 'forbidden' | 'failed' (null while it works).
  const [error, setError] = useState(null);

  // AI job polling state
  const [aiStatus, setAiStatus] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const pollingStartedAt = useRef(null);

  // Bulk review
  const [reviewingAll, setReviewingAll] = useState(false);
  const [reviewAllError, setReviewAllError] = useState(null);

  // Re-analyze button + modal state
  const [reanalyzeModalOpen, setReanalyzeModalOpen] = useState(false);
  const [reanalyzeSubmitting, setReanalyzeSubmitting] = useState(false);
  // rateLimitUntil: epoch ms at which the rate limit expires (null = not rate-limited)
  const [rateLimitUntil, setRateLimitUntil] = useState(() => {
    // Rehydrate from localStorage on mount
    return null; // courseId is not available in useState initializer — set in useEffect below
  });
  // Countdown text for the rate-limit disabled state, updated every minute
  const [rateLimitCountdown, setRateLimitCountdown] = useState('');

  // DAG visualization
  const [dagOpen, setDagOpen] = useState(false);
  const [dagNodes, setDagNodes] = useState([]);
  const [dagEdges, setDagEdges] = useState([]);

  // Elapsed-seconds counter shown on the Re-analyze button while a job runs
  const [reanalyzeElapsed, setReanalyzeElapsed] = useState(0);

  // Load modules and taxonomy
  const loadData = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError(null);
    try {
      const [modulesRes, taxonomyRes] = await Promise.all([
        getCourseModules(courseId),
        // Non-fatal: the taxonomy endpoint 404s when the backend has no skills catalogue
        // module deployed. Degrade to an empty list + a flag, never a page-level error.
        getSkillTaxonomy().then(
          (res) => { setTaxonomyUnavailable(false); return res; },
          () => { setTaxonomyUnavailable(true); return []; },
        ),
      ]);
      const mods = unwrapList(modulesRes);
      const sorted = [...mods].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      setModules(sorted);
      setTaxonomy(unwrapList(taxonomyRes));
    } catch (err) {
      // Only the kind of failure is kept: the screen says it in the page's own words, never in
      // the server's (often English) text.
      setError(err?.code === 403 ? 'forbidden' : 'failed');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Rebuild DAG layout whenever modules change
  useEffect(() => {
    if (modules.length === 0) {
      setDagNodes([]);
      setDagEdges([]);
      return;
    }
    const { nodes, edges } = buildDagLayout(modules, t);
    setDagNodes(nodes);
    setDagEdges(edges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules]);

  // Rehydrate rate-limit state from localStorage on mount / courseId change
  useEffect(() => {
    if (!courseId) return;
    const stored = getStoredUnlockTime(courseId);
    if (stored && stored > Date.now()) {
      setRateLimitUntil(stored);
    } else if (stored) {
      // Expired — clean up
      clearStoredUnlockTime(courseId);
      setRateLimitUntil(null);
    }
  }, [courseId]);

  // Update the countdown text every minute while rate-limited
  useEffect(() => {
    if (!rateLimitUntil) {
      setRateLimitCountdown('');
      return;
    }

    const update = () => {
      const remaining = rateLimitUntil - Date.now();
      if (remaining <= 0) {
        setRateLimitUntil(null);
        setRateLimitCountdown('');
        clearStoredUnlockTime(courseId);
      } else {
        setRateLimitCountdown(humanReadableDuration(remaining, t));
      }
    };

    update(); // run immediately
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateLimitUntil, courseId]);

  // Fetch AI status once on mount, decide whether to start polling
  useEffect(() => {
    if (!courseId) return;
    let active = true;
    getCourseAiStatus(courseId)
      .then((status) => {
        if (!active) return;
        setAiStatus(status);
        if (status.status === 'running') {
          setPollingActive(true);
          pollingStartedAt.current = Date.now();
        }
      })
      .catch(() => {
        // Non-fatal: AI status is informational only
      });
    return () => { active = false; };
  }, [courseId]);

  // Poll AI status
  const checkAiStatus = useCallback(async () => {
    if (!courseId) return;

    // Check timeout
    if (pollingStartedAt.current && Date.now() - pollingStartedAt.current > POLLING_TIMEOUT_MS) {
      setPollingActive(false);
      setPollingTimedOut(true);
      return;
    }

    try {
      const status = await getCourseAiStatus(courseId);
      setAiStatus(status);
      if (status.status !== 'running') {
        setPollingActive(false);
        if (status.status === 'completed') {
          // Refresh skills data when job completes
          loadData();
        }
      }
    } catch (_err) {
      // Polling failure is silent
    }
  }, [courseId, loadData]);

  usePolling(checkAiStatus, POLLING_INTERVAL, pollingActive);

  // Derive jobRunning here so the elapsed-counter effect can use it without violating rules-of-hooks
  // (hooks must not appear after the loading/error early returns in the render section)
  const jobRunning = pollingActive && aiStatus?.status === 'running';

  // Increment elapsed-seconds counter every second while a job is running
  useEffect(() => {
    if (!jobRunning) {
      setReanalyzeElapsed(0);
      return;
    }
    const id = setInterval(() => {
      setReanalyzeElapsed((s) => s + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [jobRunning]);

  // Update a single module's state immutably
  const handleModuleUpdate = useCallback((moduleId, updater) => {
    setModules((prev) => prev.map((m) => (m.id === moduleId ? updater(m) : m)));
  }, []);

  const unreviewedSkillModules = modules.filter(
    (m) => m.aiProposedSkills === true && m.skillsReviewedAt == null
  );

  const handleReviewAll = async () => {
    setReviewingAll(true);
    setReviewAllError(null);
    try {
      await reviewAllSkills(courseId);
      setModules((prev) =>
        prev.map((m) =>
          m.aiProposedSkills && !m.skillsReviewedAt
            ? { ...m, aiProposedSkills: false, skillsReviewedAt: new Date().toISOString() }
            : m
        )
      );
    } catch (err) {
      setReviewAllError(extractErrorMessage(err, t('courses.acceptAllSkillsFailed')));
    } finally {
      setReviewingAll(false);
    }
  };

  const handleReanalyze = async () => {
    setReanalyzeSubmitting(true);
    try {
      await triggerReanalysis(courseId);
      // 202 Accepted
      setReanalyzeModalOpen(false);
      toast.success(t('courses.reanalyzeStartedToast'));
      // Start polling so the in-progress banner appears
      setPollingActive(true);
      pollingStartedAt.current = Date.now();
      setPollingTimedOut(false);
    } catch (err) {
      setReanalyzeModalOpen(false);

      // The API client rejects { message, code, ...response body }: code is the HTTP status.
      const status = err?.code;
      if (status === 429) {
        // Seconds to wait: the body's retryAfter, else a Retry-After header when the error has one.
        const retryAfterSec = parseInt(
          err?.retryAfter ?? err?.response?.headers?.['retry-after'] ?? '0',
          10
        );
        if (retryAfterSec > 0) {
          const unlockMs = Date.now() + retryAfterSec * 1000;
          setRateLimitUntil(unlockMs);
          setStoredUnlockTime(courseId, unlockMs);
          const phrase = humanReadableDuration(retryAfterSec * 1000, t);
          toast.error(t('courses.rateLimitedToast', { phrase }));
        } else {
          toast.error(t('courses.rateLimitedDailyToast'));
        }
      } else if (status === 503) {
        toast.error(t('courses.aiDisabledToast'));
      } else if (status === 403) {
        toast.error(t('courses.reanalyzeNoPermissionToast'));
      } else {
        const msg = extractErrorMessage(err, t('courses.unknownErrorFallback'));
        toast.error(t('courses.reanalyzeFailedToast', { msg }));
      }
    } finally {
      setReanalyzeSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="sp-tab">
        <LoadingState skeleton="list" rows={3} message={t('courses.loadingSkillsPrereqs')} />
      </div>
    );
  }

  if (error) {
    const forbidden = error === 'forbidden';
    return (
      <div className="sp-tab">
        <InlineEmpty
          kind={forbidden ? 'lock' : 'error'}
          title={forbidden ? t('courses.noAccess') : t('ui.errorTitle')}
          description={forbidden ? undefined : t('ui.errorDescription')}
          action={forbidden ? undefined : (
            <button type="button" className="ds-btn ds-btn-ink ds-btn-sm" onClick={loadData}>
              <RefreshCw aria-hidden="true" />
              {t('courses.tryAgain')}
            </button>
          )}
        />
      </div>
    );
  }

  const runningMinutes = aiStatus?.startedAt
    ? Math.floor((Date.now() - new Date(aiStatus.startedAt).getTime()) / 60000)
    : null;

  // jobRunning is derived above the early returns (near usePolling) to allow hook usage
  const rateLimited = !!(rateLimitUntil && rateLimitUntil > Date.now());
  const reanalyzeDisabled = jobRunning || rateLimited || reanalyzeSubmitting;

  let reanalyzeLabel = t('courses.reanalyzeButton');
  if (jobRunning) {
    reanalyzeLabel = t('courses.reanalyzingElapsed', { seconds: reanalyzeElapsed });
  } else if (rateLimited && rateLimitCountdown) {
    reanalyzeLabel = t('courses.reanalyzeAvailableIn', { countdown: rateLimitCountdown });
  }

  return (
    <div className="sp-tab">
      {/* AI job in-progress banner */}
      {pollingActive && aiStatus?.status === 'running' && (
        <div className="ui-alert sp-banner" data-tone="info" role="status">
          <span className="ds-spinner ds-spinner--sm" aria-hidden="true" />
          <div>
            {t('courses.aiAnalyzingBanner')}
            {runningMinutes != null && runningMinutes > 0 && ` ${runningMinutes === 1 ? t('courses.startedAgoOne') : t('courses.startedAgoCount', { count: runningMinutes })}`}
          </div>
        </div>
      )}
      {pollingTimedOut && (
        <div className="ui-alert sp-banner" data-tone="warning" role="status">
          <div>{t('courses.jobTimedOutBanner')}</div>
        </div>
      )}

      {/* Re-analyze button row */}
      <div className="sp-reanalyze-row">
        <button
          type="button"
          className="ds-btn ds-btn-secondary ds-btn-sm sp-btn-reanalyze"
          onClick={() => setReanalyzeModalOpen(true)}
          disabled={reanalyzeDisabled}
          aria-busy={jobRunning}
          title={
            jobRunning
              ? t('courses.reanalyzeRunningTooltip')
              : rateLimited
              ? t('courses.rateLimitedTooltip', { countdown: rateLimitCountdown || '...' })
              : t('courses.reanalyzeTooltip')
          }
        >
          {jobRunning
            ? <span className="ds-spinner ds-spinner--sm" aria-hidden="true" />
            : <RefreshCw aria-hidden="true" />}
          {reanalyzeLabel}
        </button>
      </div>

      {/* Confirmation modal */}
      {reanalyzeModalOpen && (
        <ReanalyzeModal
          onConfirm={handleReanalyze}
          onCancel={() => setReanalyzeModalOpen(false)}
          submitting={reanalyzeSubmitting}
        />
      )}

      {/* ============================================================
          SKILLS SECTION
          ============================================================ */}
      <section className="sp-section" aria-labelledby="sp-skills-title">
        <div className="sp-section-header">
          <div className="sp-section-text">
            <h3 className="sp-section-title" id="sp-skills-title">{t('courses.skillsHeading')}</h3>
            <p className="sp-section-sub">
              {t('courses.skillsSubtitle')}
            </p>
          </div>
          <button
            type="button"
            className="ds-btn ds-btn-secondary ds-btn-sm sp-btn-accept-all"
            onClick={handleReviewAll}
            disabled={reviewingAll || unreviewedSkillModules.length === 0}
            title={unreviewedSkillModules.length === 0 ? t('courses.noUnreviewedModules') : t('courses.acceptAllTooltip')}
          >
            <Check aria-hidden="true" />
            {reviewingAll ? t('courses.accepting') : t('courses.acceptAll')}
          </button>
        </div>

        {reviewAllError && (
          <div className="ui-alert sp-banner" role="alert">
            <div>{reviewAllError}</div>
          </div>
        )}

        {modules.length === 0 ? (
          <InlineEmpty kind="docs" title={t('courses.noModulesFound')} />
        ) : (
          <div className="sp-module-list">
            {modules.map((mod) => (
              <ModuleSkillRow
                key={mod.id}
                mod={mod}
                courseId={courseId}
                taxonomy={taxonomy}
                taxonomyUnavailable={taxonomyUnavailable}
                onModuleUpdate={handleModuleUpdate}
              />
            ))}
          </div>
        )}
      </section>

      {/* ============================================================
          PREREQUISITES SECTION
          ============================================================ */}
      <section className="sp-section" aria-labelledby="sp-prereq-title">
        <div className="sp-section-header">
          <div className="sp-section-text">
            <h3 className="sp-section-title" id="sp-prereq-title">{t('courses.prerequisitesHeading')}</h3>
            <p className="sp-section-sub">
              {t('courses.prerequisitesSubtitle')}
            </p>
          </div>
          <button
            type="button"
            className="ds-btn ds-btn-secondary ds-btn-sm"
            onClick={() => setDagOpen((v) => !v)}
            aria-expanded={dagOpen}
          >
            <Network aria-hidden="true" />
            {dagOpen ? t('courses.hideGraph') : t('courses.showGraph')}
          </button>
        </div>

        {dagOpen && (
          <div className="sp-dag-container">
            {dagNodes.length === 0 ? (
              <div className="sp-dag-empty">{t('courses.noPrereqEdges')}</div>
            ) : (
              <ReactFlow
                nodes={dagNodes}
                edges={dagEdges}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                fitView
                attributionPosition="bottom-right"
              >
                <Background color="var(--color-border-strong)" gap={16} />
                <Controls showInteractive={false} />
              </ReactFlow>
            )}
          </div>
        )}

        {modules.length < 2 ? (
          <InlineEmpty kind="idea" title={t('courses.needTwoModules')} />
        ) : (
          <div className="sp-prereq-table-wrap sp-prereq-wrap">
            <table className="sp-prereq-table ui-table">
              <thead>
                <tr>
                  <th>{t('courses.colModule')}</th>
                  <th>{t('courses.colRequires')}</th>
                  <th>{t('courses.colAddPrerequisite')}</th>
                  <th>{t('courses.colReview')}</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((mod) => (
                  <PrereqRow
                    key={mod.id}
                    mod={mod}
                    allModules={modules}
                    courseId={courseId}
                    onModuleUpdate={handleModuleUpdate}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default SkillsPrerequisitesTab;
